const { joinRoutes, convertRouteToMqttTopic } = require('./utils');
const { errorHandlingMiddlewareBuilder } = require('./middleware');
const MqttRouter = require('./router');

/** A catch-all error handling middleware.
 * This is used to prevent errors from being thrown down the line while handling messages.
 * Logs the error to stdout and any related info (message, params, topic).
 *
 * If this behaviour is not desired, then a custom errorHandlingMiddleware should be registered
 * when initializing WrappedMQTTClient.
 */
const errorHandlingMiddleware = errorHandlingMiddlewareBuilder(
	({ error, message, params, topic }) => {
		console.error(error);
		console.error(`
        topic: ${topic || '__NO-TOPIC__'}
        message: ${message || '__NO_MESSAGE__'}
        params: ${params || '__NO_PARAMS__'}
    `);
		console.error(
			`Consider creating a custom error handling middleware with 'mqtt-sub-router.utils.errorHandlingMiddlewareBuilder'`
		);
	}
);

/**
 * Wrapper around an mqtt client that allows for using [Routers]{@link MqttRouter}
 * to subscribe to and listen for messages on topics.
 * @template ExtraHandlerFuncArgs
 */
class WrappedMQTTClient {
	/**
	 * @param {import('./types').Client} mqttClient
	 * @param {Array<import('./types').MqttMiddleware<ExtraHandlerFuncArgs>>} middlewares
	 * @param {string?} sharedGroup
	 * @param {import('mqtt').IClientSubscribeOptions?} defaultSubscriptionOptions
	 */
	constructor(
		mqttClient,
		middlewares = [],
		sharedGroup = null,
		defaultSubscriptionOptions = null
	) {
		/** @private
		 * @type {MqttRouter<ExtraHandlerFuncArgs>}
		 */
		this.indexRouter = new MqttRouter('__WrappedMqttClientRouter__', [
			...(middlewares || []),
			errorHandlingMiddleware,
		]);

		/** @private @type {string}*/
		this.sharedGroup = (sharedGroup || '').trim();
		this.sharedGroup = this.sharedGroup === '' ? null : this.sharedGroup;

		this.mqttClient = mqttClient;

		/** @private @type {import('mqtt').IClientSubscribeOptions} */
		this.defaultSubscriptionOptions = defaultSubscriptionOptions || {};

		/** @private */
		this._setupSubscriptions = this._setupSubscriptions.bind(this);
		this.listen = this.listen.bind(this);
		this.unlisten = this.unlisten.bind(this);

		this.mqttClient.on('message', (topic, message) =>
			this.indexRouter.onNewMessage({
				message: (message || '').toString(),
				params: {},
				topic,
				originalTopic: topic,
				mqttClient: this.mqttClient,
			})
		);
	}

	get routes() {
		return this.indexRouter.routes;
	}

	get mqttTopics() {
		return this.indexRouter.mqttTopics;
	}

	get connected() {
		return this.mqttClient.connected;
	}

	get disconnected() {
		return this.mqttClient.disconnected;
	}

	get disconnecting() {
		return this.mqttClient.disconnecting;
	}

	/**
	 * Register a handler for messages on a route.
	 *
	 * Note that this operation is NOT IDEMPOTENT.
	 * Calling #listen twice with the same route, will result in two handlers being invoked
	 * 	when a new message is received on that route.
	 * @param {string} route
	 * @param {MqttRouter<ExtraHandlerFuncArgs>|import('./types').MessageHandlerFunc<ExtraHandlerFuncArgs>} handler
	 * @param {import('mqtt').IClientSubscribeOptions} options
	 * @param {(err, granted: {topic: string, qos: 0|1|2}[]) => void} callback
	 * 	Function to call upon subscribe completed
	 */
	listen(route, handler, options = null, callback = undefined) {
		const newTopics = [];

		const routeAsMqttTopic = convertRouteToMqttTopic(route);
		if (handler instanceof MqttRouter) {
			newTopics.push(
				...handler.mqttTopics.map(subTopic => ({
					topic: joinRoutes(routeAsMqttTopic, subTopic),
					options: options || {},
				}))
			);
		} else {
			newTopics.push({ topic: routeAsMqttTopic, options: options || {} });
		}

		this.indexRouter.listen(route, handler);
		this._setupSubscriptions(newTopics, callback);

		return this;
	}

	/**
	 * Subscribe to topics with the underlying mqtt client
	 * @private
	 * @param {Array<{topic: string, options: import('mqtt').IClientSubscribeOptions}>} topicsAndOptions
	 * @param {(err: Error?) => void} callback For each of the entries in `topicsAndOptions`,
	 * 	this callback is invoked when the subscription completes, with or without failure
	 */
	_setupSubscriptions(topicsAndOptions, callback = undefined) {
		topicsAndOptions.forEach(entry => {
			const { topic, options } = entry;
			this.mqttClient.subscribe(
				this.sharedGroup ? joinRoutes(`$share/${this.sharedGroup}`, topic) : topic,
				{ ...this.defaultSubscriptionOptions, ...(options || {}) },
				callback
			);
		});
	}

	/** @param {string} mqttTopic
	 * @param {(err?: Error) => void} callback
	 */
	unlisten(mqttTopic, callback = undefined) {
		this.mqttClient.unsubscribe(mqttTopic, callback);
		this.indexRouter.unlisten(mqttTopic);
		return this;
	}
}

module.exports = WrappedMQTTClient;
